export const HAPANA_ENVIRONMENTS = ['local', 'dev', 'staging', 'production'] as const

export type HapanaEnvironment = (typeof HAPANA_ENVIRONMENTS)[number]

const LOCAL_HOSTNAMES = ['localhost', '127.0.0.1', '[::1]']

/**
 * Matched against the apex domain and any subdomain of it, never as a bare
 * suffix — otherwise an attacker-controlled `evil-hapana-app.com` would be
 * read as production.
 */
const ENVIRONMENT_BY_DOMAIN: ReadonlyArray<readonly [string, HapanaEnvironment]> = [
  ['hapana-dev.com', 'dev'],
  ['hapana-staging.com', 'staging'],
  ['hapana-app.com', 'production'],
]

export const HAPANA_BASE_DOMAINS: readonly string[] = ENVIRONMENT_BY_DOMAIN.map(
  ([domain]) => domain
)

/**
 * The hostname is the only signal that actually differs between deployments.
 * Vite's build mode does not: CI builds every environment with a bare
 * `vite build` (no --mode), so `import.meta.env.MODE` is "production" on dev
 * and staging too.
 *
 * Anything unrecognised — including feature environments — is treated as
 * production, so a new or misconfigured domain keeps the default brand mark
 * rather than presenting itself as a safe-to-break environment.
 */
export function getEnvironmentFromHostname(hostname: string): HapanaEnvironment {
  if (LOCAL_HOSTNAMES.includes(hostname) || hostname.endsWith('.localhost')) {
    return 'local'
  }

  for (const [domain, environment] of ENVIRONMENT_BY_DOMAIN) {
    if (hostname === domain || hostname.endsWith(`.${domain}`)) {
      return environment
    }
  }

  return 'production'
}

export function getEnvironment(): HapanaEnvironment {
  if (typeof window === 'undefined') {
    return 'production'
  }

  return getEnvironmentFromHostname(window.location.hostname)
}

export function isLocalEnvironment(): boolean {
  return getEnvironment() === 'local'
}

/**
 * Production keeps the untinted brand mark; local, dev and staging get the same
 * glyph in a different colour so the browser tab says which one it is.
 * Paths resolve against each app's `public/` directory.
 */
const FAVICON_BY_ENVIRONMENT: Readonly<Record<HapanaEnvironment, string>> = {
  local: '/favicon-local.ico',
  dev: '/favicon-dev.ico',
  staging: '/favicon-staging.ico',
  production: '/favicon.ico',
}

export function applyEnvironmentFavicon(): void {
  if (typeof document === 'undefined') {
    return
  }

  const environment = getEnvironment()
  if (environment === 'production') {
    return
  }

  const existingIcon = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
  const icon = existingIcon ?? document.createElement('link')
  icon.rel = 'icon'
  icon.href = FAVICON_BY_ENVIRONMENT[environment]

  if (!existingIcon) {
    document.head.appendChild(icon)
  }
}
